import { SysUser_OutputPayload } from '@/app/types/core/identitiesTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logger } from '../../utils/logger';

interface MultiUserState {
    // Current active user data
    user: SysUser_OutputPayload | null;
    error: string | null;
    
    // Multi-account user data cache
    userCache: Record<string, SysUser_OutputPayload>; // accountId -> user data
    userErrors: Record<string, string>; // accountId -> error message
    
    // Loading states
    isLoadingUser: boolean;
    isSwitchingUser: boolean;
    
    // User data management
    lastUserDataUpdate: number | null;
    userDataVersion: number;
}

const initialState: MultiUserState = {
    // Current active user data
    user: null,
    error: null,
    
    // Multi-account user data cache
    userCache: {},
    userErrors: {},
    
    // Loading states
    isLoadingUser: false,
    isSwitchingUser: false,
    
    // User data management
    lastUserDataUpdate: null,
    userDataVersion: 1,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        // Backward compatibility
        setUser: (state, action: PayloadAction<{ user: SysUser_OutputPayload, error: string }>) => {
            state.user = action.payload.user;
            state.error = action.payload.error;
            state.lastUserDataUpdate = Date.now();
            state.userDataVersion += 1;
        },
        
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
        
        clearUser: (state) => {            
            state.user = null;
            state.error = null;
            state.userCache = {};
            state.userErrors = {};
            state.isLoadingUser = false;
            state.isSwitchingUser = false;
            state.lastUserDataUpdate = null;
            state.userDataVersion = 1;
        },
        
        clearError: (state) => {
            state.error = null;
        },
        
        // Multi-account user management
        startLoadingUser: (state) => {
            state.isLoadingUser = true;
            state.error = null;
        },
        
        completeLoadingUser: (state, action: PayloadAction<{
            accountId: string;
            user: SysUser_OutputPayload;
        }>) => {
            state.isLoadingUser = false;
            state.user = action.payload.user;
            state.userCache[action.payload.accountId] = action.payload.user;
            state.error = null;
            delete state.userErrors[action.payload.accountId];
            state.lastUserDataUpdate = Date.now();
            state.userDataVersion += 1;
        },
        
        failLoadingUser: (state, action: PayloadAction<{
            accountId?: string;
            error: string;
        }>) => {
            state.isLoadingUser = false;
            state.error = action.payload.error;
            if (action.payload.accountId) {
                state.userErrors[action.payload.accountId] = action.payload.error;
            }
        },
        
        // User switching
        startSwitchingUser: (state) => {
            state.isSwitchingUser = true;
            state.error = null;
        },
        
        completeSwitchingUser: (state, action: PayloadAction<{
            accountId: string;
            user: SysUser_OutputPayload;
        }>) => {
            state.isSwitchingUser = false;
            state.user = action.payload.user;
            state.userCache[action.payload.accountId] = action.payload.user;
            state.error = null;
            delete state.userErrors[action.payload.accountId];
            state.lastUserDataUpdate = Date.now();
            state.userDataVersion += 1;
        },
        
        failSwitchingUser: (state, action: PayloadAction<string>) => {
            state.isSwitchingUser = false;
            state.error = action.payload;
        },
        
        // User cache management
        cacheUserData: (state, action: PayloadAction<{
            accountId: string;
            user: SysUser_OutputPayload;
        }>) => {
            state.userCache[action.payload.accountId] = action.payload.user;
            delete state.userErrors[action.payload.accountId];
        },
        
        removeCachedUserData: (state, action: PayloadAction<string>) => {
            delete state.userCache[action.payload];
            delete state.userErrors[action.payload];
        },
        
        updateCachedUserData: (state, action: PayloadAction<{
            accountId: string;
            updates: Partial<SysUser_OutputPayload>;
        }>) => {
            const cachedUser = state.userCache[action.payload.accountId];
            if (cachedUser) {
                state.userCache[action.payload.accountId] = {
                    ...cachedUser,
                    ...action.payload.updates
                };
                
                // Update current user if it's the same account
                if (state.user && state.user.id === cachedUser.id) {
                    state.user = {
                        ...state.user,
                        ...action.payload.updates
                    };
                }
            }
        },
        
        // User data isolation (clear data when switching accounts)
        clearCurrentUserData: (state) => {
            state.user = null;
            state.error = null;
            state.isLoadingUser = false;
            state.isSwitchingUser = false;
        },
        
        // Cache management
        clearUserCache: (state) => {
            state.userCache = {};
            state.userErrors = {};
        },
        
        clearUserError: (state, action: PayloadAction<string>) => {
            delete state.userErrors[action.payload];
            // Clear main error if it matches
            if (state.error === state.userErrors[action.payload]) {
                state.error = null;
            }
        },
        
        clearAllUserErrors: (state) => {
            state.error = null;
            state.userErrors = {};
        },
        
        // User data refresh
        refreshUserData: (state, action: PayloadAction<{
            accountId: string;
            user: SysUser_OutputPayload;
        }>) => {
            // Update cache
            state.userCache[action.payload.accountId] = action.payload.user;
            
            // Update current user if it matches
            if (state.user && state.user.id === action.payload.user.id) {
                state.user = action.payload.user;
            }
            
            state.lastUserDataUpdate = Date.now();
            state.userDataVersion += 1;
        },
        
        // Bulk operations
        setCachedUsers: (state, action: PayloadAction<Record<string, SysUser_OutputPayload>>) => {
            state.userCache = { ...state.userCache, ...action.payload };
        },
        
        // Profile picture update
        setProfilePictureUrl: (state, action: PayloadAction<string | null>) => {
            if (state.user) {
                state.user = { ...state.user, profilePictureUrl: action.payload };
            }
            // Sync cache entry if we can identify the account
            for (const accountId of Object.keys(state.userCache)) {
                if (state.userCache[accountId].id === state.user?.id) {
                    state.userCache[accountId] = {
                        ...state.userCache[accountId],
                        profilePictureUrl: action.payload,
                    };
                }
            }
            state.lastUserDataUpdate = Date.now();
            state.userDataVersion += 1;
        },

        // User profile updates
        updateUserProfile: (state, action: PayloadAction<{
            accountId: string;
            profileUpdates: Partial<SysUser_OutputPayload>;
        }>) => {
            const { accountId, profileUpdates } = action.payload;
            
            // Update cache
            if (state.userCache[accountId]) {
                state.userCache[accountId] = {
                    ...state.userCache[accountId],
                    ...profileUpdates
                };
            }
            
            // Update current user if it matches
            if (state.user && state.userCache[accountId] && state.user.id === state.userCache[accountId].id) {
                state.user = {
                    ...state.user,
                    ...profileUpdates
                };
            }
            
            state.lastUserDataUpdate = Date.now();
            state.userDataVersion += 1;
        },
    },
    extraReducers: (builder) => {
        // Handle loadUserDataThunk
        builder
            .addMatcher(
                (action) => action.type === 'auth/loadUserData/pending',
                (state, _action: any) => {
                    logger.redux('userSlice: loadUserData pending');
                    state.isLoadingUser = true;
                }
            )
            .addMatcher(
                (action) => action.type === 'auth/loadUserData/fulfilled',
                (state, action: any) => {
                    logger.redux('userSlice: loadUserData fulfilled', action.payload);
                    const { accountId, user } = action.payload;
                    state.userCache[accountId] = user;
                    state.user = user;
                    state.isLoadingUser = false;
                    state.isSwitchingUser = false; // Clear switching state when user data is loaded
                    state.error = null;
                    delete state.userErrors[accountId];
                    state.lastUserDataUpdate = Date.now();
                    state.userDataVersion += 1;
                    logger.redux('userSlice: User data updated, isSwitchingUser set to false, userDataVersion:', state.userDataVersion);
                }
            )
            .addMatcher(
                (action) => action.type === 'auth/loadUserData/rejected',
                (state, action: any) => {
                    logger.redux('userSlice: loadUserData rejected', action.payload);
                    state.isLoadingUser = false;
                    state.isSwitchingUser = false; // Clear switching state even on error
                    state.error = action.payload || 'Failed to load user data';
                }
            );
        
        // Handle switchAccountThunk completion
        builder
            .addMatcher(
                (action) => action.type === 'auth/switchAccount/fulfilled',
                (_state, _action: any) => {
                    // Account switch completed, but user data loading might still be in progress
                    // The isSwitchingUser will be cleared when loadUserData completes
                }
            )
            .addMatcher(
                (action) => action.type === 'auth/switchAccount/rejected',
                (state, action: any) => {
                    state.isSwitchingUser = false;
                    state.error = action.payload || 'Failed to switch account';
                }
            );
    },
});

export const {
    // Backward compatibility
    setUser,
    setError,
    clearUser,
    clearError,
    
    // Multi-account user management
    startLoadingUser,
    completeLoadingUser,
    failLoadingUser,
    
    // User switching
    startSwitchingUser,
    completeSwitchingUser,
    failSwitchingUser,
    
    // User cache management
    cacheUserData,
    removeCachedUserData,
    updateCachedUserData,
    
    // User data isolation
    clearCurrentUserData,
    
    // Cache management
    clearUserCache,
    clearUserError,
    clearAllUserErrors,
    
    // User data refresh
    refreshUserData,
    setCachedUsers,
    
    // Profile picture
    setProfilePictureUrl,

    // User profile updates
    updateUserProfile,
} = userSlice.actions;

export default userSlice.reducer;

// Selectors for multi-user state
export const selectUser = (state: { user: MultiUserState }) => state.user;
export const selectCurrentUser = (state: { user: MultiUserState }) => state.user.user;
export const selectUserError = (state: { user: MultiUserState }) => state.user.error;
export const selectIsLoadingUser = (state: { user: MultiUserState }) => state.user.isLoadingUser;
export const selectIsSwitchingUser = (state: { user: MultiUserState }) => state.user.isSwitchingUser;
export const selectUserCache = (state: { user: MultiUserState }) => state.user.userCache;
export const selectUserErrors = (state: { user: MultiUserState }) => state.user.userErrors;
export const selectLastUserDataUpdate = (state: { user: MultiUserState }) => state.user.lastUserDataUpdate;
export const selectUserDataVersion = (state: { user: MultiUserState }) => state.user.userDataVersion;

// Computed selectors
export const selectCachedUserById = (accountId: string) => (state: { user: MultiUserState }) =>
    state.user.userCache[accountId] || null;

export const selectUserErrorById = (accountId: string) => (state: { user: MultiUserState }) =>
    state.user.userErrors[accountId] || null;

export const selectHasCachedUsers = (state: { user: MultiUserState }) =>
    Object.keys(state.user.userCache).length > 0;

export const selectCachedUserCount = (state: { user: MultiUserState }) =>
    Object.keys(state.user.userCache).length;

export const selectIsUserOperationInProgress = (state: { user: MultiUserState }) =>
    state.user.isLoadingUser || state.user.isSwitchingUser;

export const selectUserDisplayName = (state: { user: MultiUserState }) => {
    const user = state.user.user;
    if (!user) return null;
    
    return user.displayName || user.userName || user.email || 'Unknown User';
};

export const selectUserInitials = (state: { user: MultiUserState }) => {
    const user = state.user.user;
    if (!user) return null;
    
    const displayName = user.displayName || user.userName || user.email || '';
    const words = displayName.split(' ').filter(word => word.length > 0);
    
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    } else {
        return 'U';
    }
};

// Export the enhanced state type
export type { MultiUserState };